// Utilidades para el calendario de metales

import {
  METAL_NEED_LEAD_TIME_DAYS,
  METAL_NEED_EXCLUDED_PERIODS,
  METAL_NEED_QUINCENA_BUCKET_COUNT,
  METAL_NEED_PROVISION_FACTOR,
} from "../../constants/MetalNeedHorizonConfig.js";

const MS_DAY = 24 * 60 * 60 * 1000;

const METAL_DISPLAY_ORDER = ["oro", "platino", "plata", "titanio"];

/**
 * Factor de conversión a metal fino según el texto del metal del Excel.
 * Detecta el quilataje aunque el texto no contenga la palabra "oro"
 * (p. ej. "18KT"), igual que hace la lógica del calendario.
 * @param {string} metalLabel
 * @returns {number} Factor de pureza (0-1); 1 para titanio/otros
 */
export const getFineFactorFromMetal = (metalLabel) => {
  const m = (metalLabel || "").toLowerCase();
  if (m.includes("platino")) return 0.955;
  if (m.includes("plata")) return 0.930;
  if (m.includes("18kt") || m.includes("18 kt")) return 0.752;
  if (m.includes("19kt") || m.includes("19 kt")) return 0.8042;
  if (m.includes("14kt") || m.includes("14 kt")) return 0.5875;
  if (m.includes("9kt") || m.includes("9 kt")) return 0.3792;
  if (m.includes("oro")) return 0.5875; // oro sin quilataje → 14kt por defecto
  return 1; // titanio u otros metales sin conversión
};

/**
 * Convierte un serial de fecha Excel a Date
 * @param {number|string} serial
 * @returns {Date|null}
 */
export const excelSerialToDate = (serial) => {
  if (serial == null || serial === "" || isNaN(serial)) return null;
  const excelEpoch = new Date("1899-12-30T00:00:00Z");
  return new Date(excelEpoch.getTime() + Number(serial) * MS_DAY);
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const addDays = (d, days) => {
  const result = new Date(d);
  result.setDate(result.getDate() + days);
  return startOfDay(result);
};

const parseConfigDate = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** Periodos excluidos parseados (inicio y fin inclusive) */
const getExcludedPeriods = () =>
  (METAL_NEED_EXCLUDED_PERIODS || []).map((p) => ({
    ...p,
    fromDate: parseConfigDate(p.from),
    toDate: parseConfigDate(p.to),
  }));

/**
 * Indica si un día cae en algún periodo excluido (vacaciones, festivos…)
 * @param {Date} date
 * @returns {boolean}
 */
export const isExcludedDay = (date) => {
  const d = startOfDay(date);
  return getExcludedPeriods().some(
    (p) => d >= p.fromDate && d <= p.toDate
  );
};

/**
 * Suma N días efectivos (excluyendo periodos no laborables) desde startDate.
 * No cuenta startDate; el primer día efectivo es el día siguiente.
 * @param {Date} startDate
 * @param {number} effectiveDays
 * @returns {Date}
 */
export const addEffectiveDays = (startDate, effectiveDays) => {
  let current = startOfDay(startDate);
  let counted = 0;
  while (counted < effectiveDays) {
    current = addDays(current, 1);
    if (!isExcludedDay(current)) counted++;
  }
  return current;
};

/**
 * Cuenta días efectivos entre fromDate (exclusivo) y toDate (inclusivo).
 * @param {Date} fromDate
 * @param {Date} toDate
 * @returns {number}
 */
export const countEffectiveDaysBetween = (fromDate, toDate) => {
  const from = startOfDay(fromDate);
  const to = startOfDay(toDate);
  if (to <= from) return 0;
  let count = 0;
  let current = addDays(from, 1);
  while (current <= to) {
    if (!isExcludedDay(current)) count++;
    current = addDays(current, 1);
  }
  return count;
};

/**
 * Fecha calendario en la que se alcanza el offset-ésimo día efectivo tras anchorDate.
 * @param {Date} anchorDate
 * @param {number} effectiveOffset
 * @returns {Date}
 */
export const getCalendarDateAtEffectiveOffset = (anchorDate, effectiveOffset) => {
  if (effectiveOffset <= 0) return startOfDay(anchorDate);
  return addEffectiveDays(anchorDate, effectiveOffset);
};

const formatShortDate = (date) =>
  date.toLocaleDateString("es-ES", { day: "numeric", month: "short" });

const formatShortDateWithYear = (date) =>
  date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });

/**
 * Etiqueta legible del rango de fechas de entrega que cubre un tramo.
 * @param {Date|null} from
 * @param {Date|null} to
 * @returns {string}
 */
export const formatBucketDateRangeLabel = (from, to) => {
  if (!from && to) return `Hasta ${formatShortDateWithYear(to)}`;
  if (from && !to) return `Desde ${formatShortDateWithYear(from)}`;
  if (from && to) {
    const sameYear = from.getFullYear() === to.getFullYear();
    const fromStr = formatShortDate(from);
    const toStr = sameYear
      ? formatShortDate(to)
      : formatShortDateWithYear(to);
    const yearSuffix = sameYear ? ` ${from.getFullYear()}` : "";
    return `${fromStr} – ${toStr}${yearSuffix}`;
  }
  return "";
};

/** Límites calendario de la quincena (1–15 o 16–fin de mes) que contiene una fecha */
const getQuincenaBoundsForDate = (date) => {
  const d = startOfDay(date);
  const year = d.getFullYear();
  const month = d.getMonth();
  if (d.getDate() <= 15) {
    return { from: new Date(year, month, 1), to: new Date(year, month, 15) };
  }
  return { from: new Date(year, month, 16), to: new Date(year, month + 1, 0) };
};

/** Inicio de la quincena siguiente a la que contiene `date` */
const getNextQuincenaStart = (date) => {
  const d = startOfDay(date);
  const year = d.getFullYear();
  const month = d.getMonth();
  if (d.getDate() <= 15) {
    return new Date(year, month, 16);
  }
  return new Date(year, month + 1, 1);
};

const getQuincenaKey = (from) => {
  const half = from.getDate() <= 15 ? "1" : "2";
  return `q_${from.getFullYear()}_${String(from.getMonth() + 1).padStart(2, "0")}_${half}`;
};

const formatQuincenaLabel = (from) => {
  const half = from.getDate() <= 15 ? "1ª" : "2ª";
  const month = from.toLocaleDateString("es-ES", { month: "short" });
  return `${half} quincena ${month}`;
};

/** Quincena con todos sus días en periodos excluidos → no se muestra columna */
const isQuincenaFullyExcluded = (from, to) => {
  let current = startOfDay(from);
  const end = startOfDay(to);
  while (current <= end) {
    if (!isExcludedDay(current)) return false;
    current = addDays(current, 1);
  }
  return true;
};

/**
 * Genera los tramos por quincenas a partir de la fecha de referencia.
 * @param {Date} referenceDate
 * @returns {Array}
 */
const buildQuincenaBuckets = (referenceDate = new Date()) => {
  const today = startOfDay(referenceDate);
  const currentQuincena = getQuincenaBoundsForDate(today);

  const buckets = [
    {
      key: "actual",
      label: "Necesidad actual",
      deliveryFrom: null,
      deliveryTo: currentQuincena.to,
      dateRangeLabel: formatBucketDateRangeLabel(null, currentQuincena.to),
    },
  ];

  let cursor = getNextQuincenaStart(currentQuincena.to);
  const maxIterations = 48;

  for (let i = 0; buckets.length < METAL_NEED_QUINCENA_BUCKET_COUNT && i < maxIterations; i++) {
    const bounds = getQuincenaBoundsForDate(cursor);
    if (!isQuincenaFullyExcluded(bounds.from, bounds.to)) {
      buckets.push({
        key: getQuincenaKey(bounds.from),
        label: formatQuincenaLabel(bounds.from),
        deliveryFrom: bounds.from,
        deliveryTo: bounds.to,
        dateRangeLabel: formatBucketDateRangeLabel(bounds.from, bounds.to),
      });
    }
    cursor = getNextQuincenaStart(bounds.to);
  }

  return buckets;
};

/**
 * Calcula los límites de fecha de entrega y etiquetas para cada tramo.
 * @param {Date} referenceDate
 * @returns {{ horizonEnd: Date, buckets: Array }}
 */
export const buildMetalNeedHorizonRanges = (referenceDate = new Date()) => {
  const today = startOfDay(referenceDate);
  const horizonEnd = addEffectiveDays(today, METAL_NEED_LEAD_TIME_DAYS);
  const buckets = buildQuincenaBuckets(referenceDate);

  return { referenceDate: today, horizonEnd, buckets, excludedPeriods: METAL_NEED_EXCLUDED_PERIODS };
};

/**
 * Clasifica un metal del Excel en categoría independiente para agregación
 * @param {string} metalLabel
 * @returns {{ key: string, label: string }}
 */
export const getMetalCategoryKey = (metalLabel) => {
  const m = (metalLabel || "").toLowerCase().trim();
  if (m.includes("platino")) return { key: "platino", label: "Platino 999" };
  if (m.includes("plata")) return { key: "plata", label: "Plata 925" };
  if (m.includes("titaneo") || m.includes("titanio")) return { key: "titanio", label: "Titanio" };
  if (m.includes("oro") || /\d+\s*kt/.test(m)) return { key: "oro", label: "Oro 999" };
  const label = (metalLabel || "Otros").trim();
  const key = label.toLowerCase().replace(/\s+/g, "_") || "otros";
  return { key, label };
};

/**
 * Asigna un pedido a su tramo según la fecha de entrega y las quincenas visibles.
 * Retrasados → «actual». Entregas posteriores al último tramo → null (no se muestran).
 * @param {Date} deliveryDate
 * @param {Date} [referenceDate]
 * @param {Array} [buckets] - Tramos generados por buildMetalNeedHorizonRanges
 * @returns {string|null} Clave del bucket
 */
export const assignMetalNeedBucket = (deliveryDate, referenceDate = new Date(), buckets = null) => {
  const today = startOfDay(referenceDate);
  const delivery = startOfDay(deliveryDate);
  const bucketList = buckets || buildMetalNeedHorizonRanges(referenceDate).buckets;

  if (delivery < today) return "actual";

  const currentQuincena = getQuincenaBoundsForDate(today);
  if (delivery <= currentQuincena.to) return "actual";

  for (const bucket of bucketList) {
    if (bucket.key === "actual" || !bucket.deliveryFrom || !bucket.deliveryTo) continue;
    const from = startOfDay(bucket.deliveryFrom);
    const to = startOfDay(bucket.deliveryTo);
    if (delivery >= from && delivery <= to) {
      return bucket.key;
    }
  }

  return null;
};

const emptyBucketCell = () => ({ grams: 0, orderIds: new Set(), orderItems: [] });

/**
 * Calcula la necesidad de metal por tramos temporales y por tipo de metal.
 * Excluye pedidos sin fecha de entrega válida.
 * @param {Array} excelData - Órdenes del Excel nocturno
 * @param {Date} [referenceDate] - Fecha de referencia (hoy)
 * @returns {{ metals: Array, invalidOrders: Array, referenceDate: Date, horizonEnd: Date, bucketDefs: Array, excludedPeriods: Array }}
 */
export const calculateMetalNeedHorizons = (excelData, referenceDate = new Date()) => {
  const invalidOrders = [];
  const metalsMap = {};
  const { horizonEnd, buckets, excludedPeriods } = buildMetalNeedHorizonRanges(referenceDate);

  const getMetalRow = (key, label) => {
    if (!metalsMap[key]) {
      const bucketCells = {};
      buckets.forEach((b) => {
        bucketCells[b.key] = emptyBucketCell();
      });
      metalsMap[key] = { key, label, buckets: bucketCells };
    }
    return metalsMap[key];
  };

  (excelData || []).forEach((item, index) => {
    const deliveryDate = excelSerialToDate(item.fecha_entrega);
    if (!deliveryDate || isNaN(deliveryDate.getTime())) {
      invalidOrders.push({
        index,
        orden: item.orden || item["1º_Ref"],
        metal: item.metal,
        fecha_entrega: item.fecha_entrega,
        reason: "fecha_entrega inválida",
      });
      return;
    }

    const metalRaw = item.metal || "";
    if (!String(metalRaw).trim()) {
      invalidOrders.push({
        index,
        orden: item.orden || item["1º_Ref"],
        metal: item.metal,
        fecha_entrega: item.fecha_entrega,
        reason: "sin metal",
      });
      return;
    }

    const { key, label } = getMetalCategoryKey(metalRaw);
    const row = getMetalRow(key, label);
    const bucketKey = assignMetalNeedBucket(deliveryDate, referenceDate, buckets);
    if (!bucketKey || !row.buckets[bucketKey]) return;

    const gramos = parseFloat(item.gramos || 0);
    const fineGrams = gramos * getFineFactorFromMetal(metalRaw) * METAL_NEED_PROVISION_FACTOR;

    const orderId = String(item.orden || item["1º_Ref"] || `row-${index}`).trim();
    const bucketCell = row.buckets[bucketKey];
    bucketCell.grams += fineGrams;

    if (bucketCell.orderIds.has(orderId)) {
      const existing = bucketCell.orderItems.find((o) => o.orden === orderId);
      if (existing) {
        existing.gramos += gramos;
        existing.fineGrams += fineGrams;
      }
    } else {
      bucketCell.orderIds.add(orderId);
      bucketCell.orderItems.push({
        orden: orderId,
        cliente: item.cliente || "",
        articulo: item.articulo || "",
        metal: String(metalRaw).trim(),
        gramos,
        fineGrams,
        fecha_entrega: item.fecha_entrega,
        deliveryDate: deliveryDate.getTime(),
      });
    }
  });

  const metals = Object.values(metalsMap)
    .sort((a, b) => {
      const ia = METAL_DISPLAY_ORDER.indexOf(a.key);
      const ib = METAL_DISPLAY_ORDER.indexOf(b.key);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.label.localeCompare(b.label, "es");
    })
    .map((m) => ({
      key: m.key,
      label: m.label,
      buckets: Object.fromEntries(
        Object.entries(m.buckets).map(([k, v]) => [
          k,
          {
            grams: v.grams,
            orders: v.orderIds.size,
            orderItems: [...v.orderItems].sort((a, b) => {
              const da = a.deliveryDate ?? 0;
              const db = b.deliveryDate ?? 0;
              if (da !== db) return da - db;
              return String(a.orden).localeCompare(String(b.orden), "es");
            }),
          },
        ])
      ),
    }));

  const today = startOfDay(referenceDate);

  return {
    metals,
    invalidOrders,
    referenceDate: today,
    horizonEnd,
    bucketDefs: buckets,
    excludedPeriods,
  };
};

/**
 * Formatea una fecha a string yyyy-mm-dd
 * @param {Date} date - Fecha a formatear
 * @returns {string} Fecha en formato yyyy-mm-dd
 */
export const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Construye una matriz de 6 semanas x 7 días para un mes dado
 * @param {number} year - Año
 * @param {number} month - Mes (0-11)
 * @returns {Date[][]} Matriz de fechas
 */
export const buildCalendarMatrix = (year, month) => {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  const day = first.getDay(); // 0=Domingo
  const offset = (day + 6) % 7; // Ajustar para que Lunes=0
  start.setDate(first.getDate() - offset);

  const matrix = [];
  for (let week = 0; week < 6; week++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      const cellDate = new Date(start);
      cellDate.setDate(start.getDate() + week * 7 + d);
      row.push(cellDate);
    }
    matrix.push(row);
  }
  return matrix;
};

/**
 * Normaliza los detalles de metales por fecha y ubicación
 * @param {Array} details - Array de registros de metales
 * @returns {Object} Mapa de necesidades por fecha
 */
export const normalizeDailyNeeds = (details) => {
  const map = {};
  if (!Array.isArray(details)) return map;
  
  for (const item of details) {
    const dateObj = item.fecha instanceof Date ? item.fecha : new Date(item.fecha);
    const key = toDateKey(dateObj);
    
    if (!map[key]) {
      map[key] = {
        cordoba: { gold: 0, platinum: 0 },
        euroshine: { gold: 0, platinum: 0 },
      };
    }
    
    const target = (item.location || "cordoba").toLowerCase();
    const loc = target.includes("euro") ? "euroshine" : "cordoba";
    map[key][loc].gold += parseFloat(item.goldRequired || 0);
    map[key][loc].platinum += parseFloat(item.platinumRequired || 0);
  }
  
  return map;
};

/**
 * Calcula la disponibilidad acumulada de metales día a día
 * @param {Date} cursor - Fecha de referencia del mes
 * @param {Object} baseStock - Stock inicial de metales
 * @param {Object} dailyNeeds - Necesidades diarias normalizadas
 * @returns {Object} Datos agregados con matriz y disponibilidad
 */
export const calculateDayAggregates = (cursor, baseStock, dailyNeeds) => {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const matrix = buildCalendarMatrix(year, month);

  let running = {
    cordoba: {
      gold: parseFloat(baseStock?.cordobaGold || 0),
      platinum: parseFloat(baseStock?.cordobaPlatinum || 0),
    },
    euroshine: {
      gold: parseFloat(baseStock?.euroshineGold || 0),
      platinum: parseFloat(baseStock?.euroshinePlatinum || 0),
    },
  };

  const result = {};
  const start = matrix[0][0];
  
  for (let i = 0; i < 42; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    const key = toDateKey(date);
    const needs = dailyNeeds[key] || { 
      cordoba: { gold: 0, platinum: 0 }, 
      euroshine: { gold: 0, platinum: 0 } 
    };

    const availableBefore = {
      cordoba: { ...running.cordoba },
      euroshine: { ...running.euroshine },
    };

    running.cordoba.gold -= needs.cordoba.gold;
    running.cordoba.platinum -= needs.cordoba.platinum;
    running.euroshine.gold -= needs.euroshine.gold;
    running.euroshine.platinum -= needs.euroshine.platinum;

    result[key] = {
      date,
      needs,
      availableBefore,
      availableAfter: {
        cordoba: { ...running.cordoba },
        euroshine: { ...running.euroshine },
      },
    };
  }
  
  return { matrix, data: result };
};

/**
 * Configuración de tamaños para el calendario
 */
export const CALENDAR_SIZES = {
  xs: { cell: 14, headerPx: 0.15, dot: 4 },
  sm: { cell: 24, headerPx: 0.25, dot: 6 },
  md: { cell: 34, headerPx: 0.5, dot: 6 },
};

/**
 * Calcula las dimensiones del calendario según el tamaño y configuración
 * @param {string} size - Tamaño del calendario
 * @param {boolean} compact - Si es vista compacta
 * @param {number} fixedHeight - Altura fija opcional
 * @param {boolean} showTitle - Si muestra título
 * @param {number} cellWidth - Ancho de celda opcional
 * @returns {Object} Dimensiones calculadas
 */
export const calculateDimensions = (size, compact, fixedHeight, showTitle, cellWidth) => {
  let dims = CALENDAR_SIZES[size] || CALENDAR_SIZES.md;
  const cellW = parseInt(cellWidth || dims.cell, 10);
  
  if (compact && fixedHeight) {
    const headerApprox = showTitle ? 44 : 28;
    const available = Math.max(fixedHeight - headerApprox, 60);
    const computedCell = Math.max(18, Math.floor(available / 6));
    dims = { 
      ...dims, 
      cell: computedCell, 
      dot: Math.min(6, Math.max(3, Math.floor(computedCell / 6))) 
    };
  }
  
  return { dims, cellW };
};

/**
 * Calcula la disponibilidad de metales día a día usando los datos de Excel
 * @param {Date} cursor - Fecha de referencia del mes
 * @param {number} stockTotalOro - Stock total de oro
 * @param {number} stockTotalPlatino - Stock total de platino
 * @param {Array} excelData - Datos de Excel con órdenes
 * @param {number} provisionFactor - Factor multiplicador del requerido (1 = normal, 1.28 = provisión para casting +28%)
 * @returns {Object} Datos agregados con matriz y disponibilidad por día
 */
export const calculateDailyAvailability = (cursor, stockTotalOro, stockTotalPlatino, excelData, provisionFactor = 1) => {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const matrix = buildCalendarMatrix(year, month);

  const excelSerialToDate = (serial) => {
    if (!serial || isNaN(serial)) return null;
    const excelEpoch = new Date('1899-12-30T00:00:00Z');
    const date = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);
    return date;
  };

  const fechasFuturas = excelData.map(item => {
    return excelSerialToDate(item.fecha_entrega);
  }).filter(date => date && date > new Date());

  let fechaMasFutura = null;
  if (fechasFuturas.length > 0) {
    fechaMasFutura = new Date(Math.max(...fechasFuturas));
  }

  const METAL_PURITY_FACTORS = {
    GOLD: {
      '9kt': 0.3792,
      '14kt': 0.5875,
      '18kt': 0.752,
      '19kt': 0.8042
    },
    PLATINUM: 0.955,
    SILVER: 0.930
  };

  const result = {};
  const start = matrix[0][0];
  
  for (let i = 0; i < 42; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    const key = toDateKey(date);
    
    if (fechaMasFutura && date > fechaMasFutura) {
      result[key] = {
        date,
        oroDisponible: null,
        platinoDisponible: null,
        requeridoOro: 0,
        requeridoPlatino: 0,
        stockTotalOro,
        stockTotalPlatino,
      };
      continue;
    }
    
    const filteredOrders = excelData.filter(item => {
      const orderDate = excelSerialToDate(item.fecha_entrega);
      if (!orderDate) return false;
      
      const normalizedOrderDate = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());
      const normalizedCurrentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      return normalizedOrderDate <= normalizedCurrentDate;
    });

    
    let requeridoOro = 0;
    let requeridoPlatino = 0;
    
    filteredOrders.forEach(item => {
      const metal = item.metal || '';
      const gramos = parseFloat(item.gramos || 0);
      
      if (metal.toLowerCase().includes('plata')) {
        return;
      }

      let purityFactor = 0;
      if (metal.toLowerCase().includes('18kt') || metal.toLowerCase().includes('18 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['18kt'];
      } else if (metal.toLowerCase().includes('14kt') || metal.toLowerCase().includes('14 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['14kt'];
      } else if (metal.toLowerCase().includes('9kt') || metal.toLowerCase().includes('9 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['9kt'];
      } else if (metal.toLowerCase().includes('19kt') || metal.toLowerCase().includes('19 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['19kt'];
      } else if (metal.toLowerCase().includes('platino')) {
        purityFactor = METAL_PURITY_FACTORS.PLATINUM;
      }

      if (purityFactor === 0) return;

      const fino = gramos * purityFactor * provisionFactor;

      if (metal.toLowerCase().includes('platino')) {
        requeridoPlatino += fino;
      } else {
        requeridoOro += fino;
      }
    });
    
    const oroDisponible = stockTotalOro - requeridoOro;
    const platinoDisponible = stockTotalPlatino - requeridoPlatino;

    result[key] = {
      date,
      oroDisponible,
      platinoDisponible,
      requeridoOro,
      requeridoPlatino,
      stockTotalOro,
      stockTotalPlatino,
    };
  }
  
  return { matrix, data: result };
};

/**
 * Calcula la cobertura del stock: hasta qué fecha aguanta el stock actual según
 * los pedidos (convertidos a fino) y cuántos días de margen quedan.
 * Un pedido "consume" stock en su fecha de entrega; el stock se agota el primer
 * día en el que la necesidad acumulada supera al stock disponible.
 * @param {number} stockTotalOro - Stock total de oro fino (g)
 * @param {number} stockTotalPlatino - Stock total de platino fino (g)
 * @param {Array} excelData - Órdenes del Excel nocturno
 * @returns {{ gold: MetalCoverage, platinum: MetalCoverage }}
 */
export const calculateMetalCoverage = (stockTotalOro, stockTotalPlatino, excelData) => {
  const excelSerialToDate = (serial) => {
    if (!serial || isNaN(serial)) return null;
    const excelEpoch = new Date("1899-12-30T00:00:00Z");
    return new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);
  };

  const FACTORS = {
    GOLD: { "9kt": 0.3792, "14kt": 0.5875, "18kt": 0.752, "19kt": 0.8042 },
    PLATINUM: 0.955,
  };

  const finoDeItem = (item) => {
    const metal = (item.metal || "").toLowerCase();
    const gramos = parseFloat(item.gramos || 0);
    if (metal.includes("plata")) return { gold: 0, platinum: 0 };

    let factor = 0;
    let esPlatino = false;
    if (metal.includes("18kt") || metal.includes("18 kt")) factor = FACTORS.GOLD["18kt"];
    else if (metal.includes("14kt") || metal.includes("14 kt")) factor = FACTORS.GOLD["14kt"];
    else if (metal.includes("9kt") || metal.includes("9 kt")) factor = FACTORS.GOLD["9kt"];
    else if (metal.includes("19kt") || metal.includes("19 kt")) factor = FACTORS.GOLD["19kt"];
    else if (metal.includes("platino")) {
      factor = FACTORS.PLATINUM;
      esPlatino = true;
    }

    if (!factor) return { gold: 0, platinum: 0 };
    const fino = gramos * factor;
    return esPlatino ? { gold: 0, platinum: fino } : { gold: fino, platinum: 0 };
  };

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOfDay(new Date());
  const MS_DAY = 24 * 60 * 60 * 1000;

  const ordersByDate = [];
  (excelData || []).forEach((item) => {
    const date = excelSerialToDate(item.fecha_entrega);
    if (!date) return;
    const fino = finoDeItem(item);
    ordersByDate.push({ date: startOfDay(date), ...fino });
  });
  ordersByDate.sort((a, b) => a.date - b.date);

  const buildCoverage = (stock, key) => {
    let acumulado = 0;
    let runoutDate = null;
    for (const order of ordersByDate) {
      acumulado += order[key];
      if (acumulado > stock) {
        runoutDate = order.date;
        break;
      }
    }

    const totalRequerido = ordersByDate.reduce((sum, o) => sum + o[key], 0);
    if (!runoutDate) {
      return {
        covered: true,
        runoutDate: null,
        days: null,
        stock,
        totalRequerido,
        remaining: stock - totalRequerido,
      };
    }

    const days = Math.max(0, Math.round((runoutDate - today) / MS_DAY));
    return {
      covered: false,
      runoutDate,
      days,
      stock,
      totalRequerido,
      remaining: stock - totalRequerido,
    };
  };

  return {
    gold: buildCoverage(parseFloat(stockTotalOro || 0), "gold"),
    platinum: buildCoverage(parseFloat(stockTotalPlatino || 0), "platinum"),
  };
};
